import { useState, useEffect } from 'react';
import { Formik } from 'formik';
import Parser from 'html-react-parser';
import { v4 as uuidv4 } from 'uuid';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useMediaQuery } from '@mui/material';
import ButtonCustom from '../../../common/__deprecated__/button-custom';
import Select from '../../../common/select';
import Input from '../../../common/input';
import './styles.scss';
import l from '../../../../lang';
import { getTextValidCharacters, selectLanguages } from '../../../../utils/common-utils';
import MessageError from '../../../common/message-error';
import Loading from '../../../common/loading';
import Message from '../../../common/message';
import { CheckoutTypes, MessageTypes } from '../../../../constants';
import { allCountries, functionalCurrenciesByCountry } from '../../../../config';
import ButtonLink from '../../../common/button-link';
import { openNewTabAndFocus } from '../../../../utils';
import { Urls } from '../../../../config/urls';
import {
  segmentIntegrationsPayButtonCeatePayButton,
  segmentIntegrationsPayButtonCopyCode,
} from '../../../../utils/analytics';
import { usePaymentIntegration } from '../../../../store/payment-integration';
import { useDashboard } from '../../../../store/dashboard';
import { segmentIntegrationsPayButtonCeatePayButtonPayload } from '../../../../utils/analytics/segment.d';
import FraudMessage from '../../fraud-message';

interface FieldValues {
  currency: string;
  amount: number | string;
  country: string;
  lang: string;
  text: string;
}

const CreatePaymentBtn = () => {
  const { loading, error, errorInfo, apiKey, currencies, getCurrenciesPI } = usePaymentIntegration();
  const { merchantMe } = useDashboard();
  const [ availableCurrencies, setAvailableCurrencies ] = useState(currencies);
  const [ blockCurrenciesSelect, setBlockCurrenciesSelect ] = useState(false);
  const { canOperate, fraudReview } = merchantMe || {};

  const isTabletMobile = useMediaQuery('(max-width:768px)');
  const [generatedScript, setGeneratedScript] = useState('');

  const setDefaultCurrency = (value: string, setFieldValue: (fieldName: string, fieldValue: string) => void) => {
    if (currencies) {
      const cs = Object.keys(currencies);
      if (cs.length && !cs.filter(d => value === d).length) {
        setFieldValue('currency', cs[0]);
      }
    }
  };

  const countries = () => {
    const countries: { [key: string]: string } = {};
    allCountries.forEach(co => {
      countries[co] = l(`countryCode.${co}`);
    });
    return countries;
  };

  const validate = (values: FieldValues) => {
    const errors: Partial<FieldValues> = {};
    if ((values.amount ?? 0) <= 0) {
      errors.amount = l('form.input.required');
    }
    if (!values.currency) {
      errors.currency = l('form.input.required');
    }
    return errors;
  };

  const createScript = (values: FieldValues) => {
    const aux: segmentIntegrationsPayButtonCeatePayButtonPayload = {
      countryCode: values.country,
      currency: values.currency,
      amount: values.amount,
      buttonText: values.text,
    };
    segmentIntegrationsPayButtonCeatePayButton(aux);

    const id = uuidv4();
    let str =
      process.env.REACT_APP_ENV === 'production'
        ? // eslint-disable-next-line max-len
          '<script data-reference-id=":referenceId">const s=document.createElement("script");s.src="https://static.dlocalgo.com/dlocalgo.min.js",s.async=!0,document.body.appendChild(s),s.addEventListener("load",()=>{const e=document.querySelector(\'script[data-reference-id=":referenceId"]\'),t=e.parentNode,n="dp-btn-:referenceId",c=document.createElement("div");c.id=n,t.insertBefore(c,e);new DlocalGo(":apiKey").createCheckout(n,{subType:":subType",country::country,currency:":currency",amount:":amount",lang:":lang",text:":text"})});</script>'
        : // eslint-disable-next-line max-len
          '<script data-reference-id=":referenceId">const s=document.createElement("script");s.src="https://static.dlocalgo.com/dlocalgo.min.js",s.async=!0,document.body.appendChild(s),s.addEventListener("load",()=>{const e=document.querySelector(\'script[data-reference-id=":referenceId"]\'),t=e.parentNode,n="dp-btn-:referenceId",c=document.createElement("div");c.id=n,t.insertBefore(c,e);new DlocalGo(":apiKey", true).createCheckout(n,{subType:":subType",country::country,currency:":currency",amount:":amount",lang:":lang",text:":text"})});</script>';

    str = str.replace(/:subType/g, CheckoutTypes.BUTTON);
    str = str.replace(/:referenceId/g, id);
    str = str.replace(/:apiKey/g, apiKey);
    str = str.replace(/:country/g, `"${values.country || ''}"`);
    str = str.replace(/:currency/g, values.currency);
    str = str.replace(/:amount/g, values.amount?.toString() ?? '');
    str = str.replace(/:lang/g, values.lang);
    str = str.replace(/:text/g, values.text);
    return str;
  };

  const formDisabled = !canOperate || fraudReview || loading;

  return (
    <div className="create-payment-btn">
      <Formik
        initialValues={{
          currency: 'USD',
          amount: '',
          country: '',
          lang: '',
          text: '',
        }}
        validate={validate}
        onSubmit={values => {
          const script = createScript(values);
          setGeneratedScript(script);
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
          useEffect(() => {
            if(values.country === merchantMe?.country){
              setBlockCurrenciesSelect(false);
              setAvailableCurrencies(currencies);
              setDefaultCurrency(values.country);
            }else if(values.country){
              setBlockCurrenciesSelect(true);
              setAvailableCurrencies({USD: "USD"});
              setDefaultCurrency(values.country);
            }
          }, [currencies])
        
          const setDefaultCurrency = (value:string) => {
            if (availableCurrencies) {
              const cs = Object.keys(availableCurrencies);
              if (cs.length && !cs.filter(d => value === d).length) {
                setFieldValue('currency', cs[0]);
              }
            }
          };


          return (
            <form onSubmit={handleSubmit}>
              {(!canOperate || (canOperate && fraudReview)) && (
                <div className="create-payment-btn__message">
                  {!canOperate && <Message type={MessageTypes.INFO} body={l('common.cannotOperateMessage')} />}
                  <FraudMessage />
                </div>
              )}
              <div className="create-payment-btn__title">{l('localCurrencyWarning')}</div>
              <div className="create-payment-btn__desc__step">{Parser(l('createPaymentBtn.desc.stepOne'))}</div>
              <div className="create-payment-btn__row">
                <div className="create-payment-btn__field create-payment-btn__field--left">
                  <Select
                    name="country"
                    label={l('createPaymentBtn.country')}
                    value={values.country}
                    onChange={e => {
                      if (e.target.value) {
                        getCurrenciesPI(e.target.value);
                      } else {
                        setFieldValue('currency', 'USD');
                      }
                      setFieldValue('country', e.target.value);
                    }}
                    disabled={formDisabled}
                    options={countries()}
                    defaultOptionEnabled
                    defaultOptionText={l('common.all.m')}
                    error={(touched.country && errors.country) || undefined}
                  />
                </div>
                <div className="create-payment-btn__field create-payment-btn__field--right">
                  <Select
                    name="currency"
                    label={l('createPaymentBtn.currency')}
                    value={values.currency}
                    onChange={handleChange}
                    options={availableCurrencies}
                    disabled={blockCurrenciesSelect || !values.country || formDisabled}
                    error={(touched.currency && errors.currency) || undefined}
                    message={
                      (blockCurrenciesSelect && 
                              merchantMe?.country || !values?.country) ? 
                              Parser(l('localCurrencyInfo', (functionalCurrenciesByCountry as {[key:string]: string})[merchantMe?.country as string])) : null
                    }
                  />
                </div>
              </div>
              <div className="create-payment-btn__row">
                <div className="create-payment-btn__field create-payment-btn__field--left">
                  <Input
                    type="number"
                    name="amount"
                    label={l('createPaymentBtn.amount')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.amount}
                    maxLength={100}
                    disabled={formDisabled}
                    error={errors.amount && touched.amount && errors.amount}
                  />
                </div>
                <div className="create-payment-btn__field create-payment-btn__field--right">
                  <Input
                    type="text"
                    name="text"
                    label={l('createPaymentBtn.text')}
                    placeholder={l('createPaymentBtn.text.placeholder')}
                    onChange={e => setFieldValue('text', getTextValidCharacters(e.target.value))}
                    onBlur={handleBlur}
                    value={values.text}
                    maxLength={50}
                    disabled={formDisabled}
                    error={(touched.text && errors.text) || undefined}
                  />
                </div>
              </div>
              {!values.text && (
                <div className="create-payment-btn__row">
                  <div className="create-payment-btn__field create-payment-btn__field--left">
                    <Select
                      name="lang"
                      label={l('createPaymentBtn.langBtn')}
                      value={values.lang}
                      onChange={handleChange}
                      defaultOptionEnabled
                      defaultOptionText={l('createPaymentBtn.langBtn.auto')}
                      options={selectLanguages()}
                      disabled={formDisabled}
                      error={(touched.lang && errors.lang) || undefined}
                    />
                  </div>
                </div>
              )}
              <div className="create-payment-btn__note-desc">
                <div>{l('createPaymentBtn.noteRequiredfields')}</div>
              </div>
              {loading && <Loading fullScreem />}
              {error && (
                <div className="create-payment-btn__error">
                  <MessageError errorInfo={errorInfo} />
                </div>
              )}
              <div className="create-payment-btn__submit">
                <ButtonCustom
                  type="submit"
                  text={l('createPaymentBtn.btn')}
                  textLoading={l('form.sending')}
                  variant="contained"
                  size={`${isTabletMobile ? 'full-square' : 'square'}`}
                  disabled={error || !apiKey || formDisabled}
                />
              </div>
              <div className="create-payment-btn__desc__step">{Parser(l('createPaymentBtn.desc.stepTwo'))}</div>
              <div className="create-payment-btn__result">
                <Input name="result" value={generatedScript} disabled multiline rows={4} />
                <div className="create-payment-btn__result__btn">
                  <CopyToClipboard text={generatedScript} onCopy={() => segmentIntegrationsPayButtonCopyCode()}>
                    <ButtonCustom
                      name="button-copy"
                      text={l('createPaymentBtn.copyCode')}
                      variant="contained"
                      size={`${isTabletMobile ? 'full-square' : 'square'}`}
                      disabled={generatedScript.trim() === ''}
                    />
                  </CopyToClipboard>
                </div>
              </div>
              <div className="create-payment-btn__note">
                <span>{Parser(l('createPaymentBtn.forDevelopers'))} </span>
                <ButtonLink
                  text={l('createPaymentBtn.forDevelopers.clickHere')}
                  onClick={() => openNewTabAndFocus(Urls.docs.paymentButton)}
                />
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreatePaymentBtn;
